// components/Admin/AutoProvisioner.jsx — silently brings the SharePoint schema up to date when the provisioning owner signs in
import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@fluentui/react-components";
import { Dismiss20Regular, DatabaseArrowUp20Regular } from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import {
  provisionSchema, getProvisionToken, needsAutoProvision, isProvisioningOwner, PROVISIONED_EVENT, SCHEMA_VERSION,
} from "../../services/provisioningService";

const SESSION_KEY = `lekoil_provision_attempted_${SCHEMA_VERSION}`;

export default function AutoProvisioner() {
  const { instance, accounts } = useMsal();
  const { currentUserEmail, isDemoMode, loading } = useUserAccess();
  const isOwner = isProvisioningOwner(currentUserEmail);
  const navigate = useNavigate();
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (loading || !isOwner || isDemoMode || !needsAutoProvision()) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {}

    (async () => {
      try {
        // Silent only — never pop a consent window at sign-in
        const token = await getProvisionToken(instance, accounts);
        const result = await provisionSchema(token);
        const changed = result.lists.filter((l) => l.status === "created" || l.status === "updated");
        if (changed.length) window.dispatchEvent(new Event(PROVISIONED_EVENT));
        if (!result.ok) {
          setNotice({ type: "error", text: "Some SharePoint lists could not be provisioned." });
        } else if (changed.length) {
          const actions = changed.reduce((n, l) => n + l.actions.length, 0);
          setNotice({ type: "success", text: `SharePoint schema updated — ${actions} change${actions === 1 ? "" : "s"} across ${changed.length} list${changed.length === 1 ? "" : "s"}.` });
        }
      } catch (err) {
        setNotice({
          type: err.code === "consent_required" ? "info" : "error",
          text: err.code === "consent_required"
            ? "SharePoint lists need provisioning. Grant the one-time permission to finish setup."
            : `Schema provisioning failed: ${err.message}`,
        });
      }
    })();
  }, [loading, isOwner, isDemoMode, instance, accounts]);

  if (!notice || !isOwner) return null;
  const bg = notice.type === "success" ? "#0B6A0B" : notice.type === "info" ? "#002D7A" : "#A4262C";
  return (
    <div role="status" style={{ position: "fixed", bottom: 24, left: 264, zIndex: 1900, maxWidth: 440, background: bg, color: "#fff", borderRadius: 10, padding: "12px 14px", boxShadow: "0 8px 28px rgba(0,0,0,0.22)", display: "flex", gap: 10, alignItems: "center", fontSize: 13, lineHeight: 1.5 }}>
      <DatabaseArrowUp20Regular style={{ flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{notice.text}</span>
      {notice.type !== "success" && (
        <Button size="small" appearance="secondary" onClick={() => { setNotice(null); navigate("/admin/schema"); }}>Review</Button>
      )}
      <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", display: "flex", padding: 2 }}>
        <Dismiss20Regular />
      </button>
    </div>
  );
}
